// src/pages/admin/AdminActivatePackPage.jsx — UI Pro Moderne v2
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, Button, Chip, Alert,
  Avatar, CircularProgress, Divider, LinearProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  InputAdornment, IconButton,
} from "@mui/material";
import SearchIcon            from "@mui/icons-material/Search";
import PersonIcon            from "@mui/icons-material/Person";
import InventoryIcon         from "@mui/icons-material/Inventory";
import CheckCircleIcon       from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon      from "@mui/icons-material/RocketLaunch";
import AccessTimeIcon        from "@mui/icons-material/AccessTime";
import CalendarTodayIcon     from "@mui/icons-material/CalendarToday";
import TrendingUpIcon        from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon             from "@mui/icons-material/Close";
import ArrowForwardIcon      from "@mui/icons-material/ArrowForward";
import { useTranslation }    from "react-i18next";
import api, { getUserMessage } from "../../api/axios";

// ── Utilitaires ───────────────────────────────────────────────────────────────
const fmt = (n) => `${Math.round(Number(n || 0))} min`;

// ── Stepper header ────────────────────────────────────────────────────────────
const STEPS = [
  { n: 1, icon: <PersonIcon sx={{ fontSize: 16 }} />,    label: "Utilisateur" },
  { n: 2, icon: <InventoryIcon sx={{ fontSize: 16 }} />, label: "Pack"        },
  { n: 3, icon: <RocketLaunchIcon sx={{ fontSize: 16 }}/>,label: "Activation" },
];

function StepBar({ active }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0} sx={{ mb: 4 }}>
      {STEPS.map((s, i) => {
        const done    = s.n < active;
        const current = s.n === active;
        const color   = done ? "#22c55e" : current ? "#6366f1" : "#cbd5e1";
        const bg      = done ? "#f0fdf4" : current ? "#eef2ff" : "#f8fafc";
        const textCol = done ? "#16a34a" : current ? "#4f46e5" : "#94a3b8";
        return (
          <React.Fragment key={s.n}>
            <Stack direction="row" spacing={1} alignItems="center"
              sx={{ transition: "all .3s" }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: "50%",
                bgcolor: bg, border: `2px solid ${color}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color, transition: "all .3s",
              }}>
                {done
                  ? <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                  : React.cloneElement(s.icon, { sx: { fontSize: 16, color } })}
              </Box>
              <Typography variant="caption" fontWeight={current ? 700 : 500}
                sx={{ color: textCol, display: { xs: "none", sm: "block" }, transition: "all .3s" }}>
                {s.label}
              </Typography>
            </Stack>
            {i < STEPS.length - 1 && (
              <Box sx={{ flex: 1, mx: 1.5, height: 2,
                bgcolor: done ? "#86efac" : "#e2e8f0",
                borderRadius: 1, transition: "background .4s" }} />
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

// ── Carte de sélection utilisateur ───────────────────────────────────────────
function UserCard({ user, selected, onClick }) {
  const isActive = user.is_active !== false;
  return (
    <Box onClick={onClick} sx={{
      p: 1.5, borderRadius: 2, cursor: "pointer",
      border: "1.5px solid",
      borderColor: selected ? "#6366f1" : "transparent",
      bgcolor: selected ? "#eef2ff" : "transparent",
      transition: "all .15s",
      "&:hover": { bgcolor: selected ? "#eef2ff" : "#f8fafc", borderColor: selected ? "#6366f1" : "#e2e8f0" },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ width: 34, height: 34, fontSize: 13,
          bgcolor: selected ? "#6366f1" : "#e2e8f0",
          color:   selected ? "#fff"    : "#64748b",
          transition: "all .15s",
        }}>
          {String(user.username || user.email || "?")[0].toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user.username || "—"}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{user.email || "—"}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {user.is_admin && (
            <Chip size="small" label="Admin"
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#eef2ff", color: "#6366f1" }} />
          )}
          <Box sx={{ width: 7, height: 7, borderRadius: "50%",
            bgcolor: isActive ? "#22c55e" : "#f59e0b" }} />
        </Stack>
      </Stack>
    </Box>
  );
}

// ── Carte de sélection pack ───────────────────────────────────────────────────
function PackCard({ pack, selected, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      p: 2, borderRadius: 2, cursor: "pointer",
      border: "1.5px solid",
      borderColor: selected ? "#6366f1" : "#e2e8f0",
      bgcolor: selected ? "#eef2ff" : "#fff",
      transition: "all .15s",
      "&:hover": { borderColor: "#a5b4fc", bgcolor: selected ? "#eef2ff" : "#f8fafc" },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" fontWeight={700}>{pack.name}</Typography>
            <Chip size="small" label={pack.code}
              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569" }} />
            {!pack.is_active && (
              <Chip size="small" label="Inactif"
                sx={{ height: 18, fontSize: 10, bgcolor: "#fef2f2", color: "#ef4444" }} />
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 13, color: "#6366f1" }} />
              <Typography variant="caption" color="text.secondary">{fmt(pack.total_minutes)}</Typography>
            </Stack>
            {pack.validity_days && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 13, color: "#22c55e" }} />
                <Typography variant="caption" color="text.secondary">{pack.validity_days} jours</Typography>
              </Stack>
            )}
          </Stack>
        </Box>
        {selected && <CheckCircleIcon sx={{ fontSize: 20, color: "#6366f1", mt: 0.5 }} />}
      </Stack>
      {pack.description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {pack.description}
        </Typography>
      )}
    </Box>
  );
}

// ── Mini KPI crédits ──────────────────────────────────────────────────────────
function CreditKpi({ label, value, color, icon }) {
  return (
    <Box sx={{ flex: 1, textAlign: "center", py: 1.5, px: 1,
      bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mb: 0.25 }}>
        {React.cloneElement(icon, { sx: { fontSize: 14, color } })}
        <Typography variant="body2" fontWeight={700} sx={{ color }}>{value}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
    </Box>
  );
}

// ── Dialog confirmation ───────────────────────────────────────────────────────
function ConfirmDialog({ open, onClose, onConfirm, busy, user, pack }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <RocketLaunchIcon sx={{ color: "#6366f1", fontSize: 20 }} />
            <Typography fontWeight={700}>Confirmer l'activation</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2}>
          <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Utilisateur</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {user ? `${user.username || user.email} (#${user.id})` : "—"}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Pack</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {pack ? `${pack.name} — ${fmt(pack.total_minutes)}` : "—"}
                </Typography>
              </Stack>
              {pack?.validity_days && (
                <>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Validité</Typography>
                    <Typography variant="caption" fontWeight={700}>{pack.validity_days} jours</Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 12 }}>
            Un wallet sera créé ou mis à jour pour cet utilisateur avec les crédits du pack.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} disabled={busy} sx={{ borderRadius: 2 }}>Annuler</Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RocketLaunchIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, flex: 1 }}>
          {busy ? "Activation..." : "Confirmer et activer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function AdminActivatePackPage() {
  const { t } = useTranslation();

  const [users,  setUsers]  = useState([]);
  const [packs,  setPacks]  = useState([]);
  const [userId, setUserId] = useState("");
  const [packId, setPackId] = useState("");
  const [qUser,  setQUser]  = useState("");
  const [qPack,  setQPack]  = useState("");

  const [before, setBefore] = useState(null);
  const [after,  setAfter]  = useState(null);
  const [credAvail, setCredAvail] = useState(true);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [loadingCred,  setLoadingCred]  = useState(false);
  const [busy,         setBusy]         = useState(false);
  const [confirmOpen,  setConfirmOpen]  = useState(false);
  const [snack,        setSnack]        = useState(null);

  const selectedUser = useMemo(() => users.find(u => String(u.id) === String(userId)) || null, [users, userId]);
  const selectedPack = useMemo(() => packs.find(p => String(p.id) === String(packId)) || null, [packs, packId]);

  const step = !userId ? 1 : !packId ? 2 : 3;

  const filteredUsers = useMemo(() => {
    if (!qUser.trim()) return users;
    const q = qUser.toLowerCase();
    return users.filter(u =>
      String(u.username || "").toLowerCase().includes(q) ||
      String(u.email || "").toLowerCase().includes(q)
    );
  }, [users, qUser]);

  const filteredPacks = useMemo(() => {
    if (!qPack.trim()) return packs;
    const q = qPack.toLowerCase();
    return packs.filter(p =>
      String(p.name || "").toLowerCase().includes(q) ||
      String(p.code || "").toLowerCase().includes(q)
    );
  }, [packs, qPack]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users/", { params: { limit: 200, offset: 0 } });
      setUsers(data.items || []);
    } catch (e) {
      setSnack({ text: "Erreur chargement utilisateurs", sev: "error" });
    } finally { setLoadingUsers(false); }
  }, []);

  const loadPacks = useCallback(async () => {
    setLoadingPacks(true);
    try {
      const { data } = await api.get("/admin/packages/packs", { params: { limit: 200 } });
      setPacks(data.items || []);
    } catch {
      try {
        const { data } = await api.get("/admin/lab-packs", { params: { limit: 200 } });
        setPacks(data.items || []);
      } catch (e) {
        setSnack({ text: "Erreur chargement packs", sev: "error" });
      }
    } finally { setLoadingPacks(false); }
  }, []);

  const loadCredits = useCallback(async (uid) => {
    if (!uid) return null;
    setLoadingCred(true);
    try {
      const { data } = await api.get(`/admin/users/${uid}/lab-credits`, { params: { limit: 20 } });
      setCredAvail(true);
      return data;
    } catch (e) {
      if (e?.response?.status === 404) setCredAvail(false);
      return null;
    } finally { setLoadingCred(false); }
  }, []);

  useEffect(() => { loadUsers(); loadPacks(); }, [loadUsers, loadPacks]);

  useEffect(() => {
    if (!userId) { setBefore(null); setAfter(null); return; }
    loadCredits(userId).then(snap => { setBefore(snap); setAfter(null); });
  }, [userId, loadCredits]);

  const doActivate = async () => {
    if (!userId || !packId) return;
    setBusy(true);
    try {
      // grant-credits crée le wallet immédiatement et de façon garantie
      const { data: grantResult } = await api.post(
        `/admin/users/${userId}/grant-credits`,
        { pack_id: Number(packId), note: `Admin activation — pack #${packId}` }
      );
      const snap = await loadCredits(userId);
      setAfter(snap);
      setSnack({
        text: `✅ Pack activé — ${grantResult.minutes_added} min attribuées · Wallet #${grantResult.wallet_id}`,
        sev: "success",
      });
      setConfirmOpen(false);
    } catch (e) {
      setSnack({ text: getUserMessage(e, "Activation impossible"), sev: "error" });
    } finally { setBusy(false); }
  };

  const diff = useMemo(() => {
    if (!before || !after) return null;
    const b = before.summary || {}, a = after.summary || {};
    return {
      remaining: Number(a.remaining_minutes || 0) - Number(b.remaining_minutes || 0),
      total:     Number(a.total_minutes    || 0) - Number(b.total_minutes    || 0),
      wallets:   Number(a.total_wallets    || 0) - Number(b.total_wallets    || 0),
    };
  }, [before, after]);

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", my: 4, px: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "flex-start" }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#eef2ff",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RocketLaunchIcon sx={{ fontSize: 20, color: "#6366f1" }} />
            </Box>
            <Typography variant="h5" fontWeight={700}>{t("admin.activate_pack_title")}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Attribue un pack de crédits labs à un utilisateur en 3 étapes.
          </Typography>
        </Box>

        <Button
          variant="contained" size="large"
          disabled={step < 3 || busy}
          onClick={() => setConfirmOpen(true)}
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: 2.5, px: 3,
            bgcolor: step === 3 ? "#6366f1" : "#e2e8f0",
            color:   step === 3 ? "#fff"    : "#94a3b8",
            "&:hover": { bgcolor: step === 3 ? "#4f46e5" : "#e2e8f0" },
            "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            transition: "all .3s",
            boxShadow: step === 3 ? "0 4px 14px rgba(99,102,241,.35)" : "none",
          }}>
          Activer le pack
        </Button>
      </Stack>

      {/* Stepper */}
      <StepBar active={step} />

      {/* Corps en deux colonnes */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">

        {/* Colonne gauche — sélections */}
        <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>

          {/* ── Sélection utilisateur ── */}
          <Paper elevation={0} sx={{
            border: "1px solid", borderColor: step >= 1 ? "divider" : "#f1f5f9",
            borderRadius: 3, overflow: "hidden",
            transition: "border-color .3s",
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider",
                bgcolor: step === 1 ? "#fafafe" : "#fff" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%",
                bgcolor: userId ? "#f0fdf4" : "#eef2ff",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {userId
                  ? <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                  : <PersonIcon sx={{ fontSize: 16, color: "#6366f1" }} />}
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Étape 1 — Utilisateur
              </Typography>
              {selectedUser && (
                <Chip size="small" label={selectedUser.username || selectedUser.email}
                  onDelete={() => { setUserId(""); setBefore(null); setAfter(null); }}
                  sx={{ height: 22, fontSize: 11, bgcolor: "#eef2ff", color: "#4f46e5", ml: "auto !important" }} />
              )}
            </Stack>
            <Box sx={{ p: 2.5 }}>
              <TextField size="small" fullWidth placeholder="Rechercher un utilisateur..."
                value={qUser} onChange={e => setQUser(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    {loadingUsers ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
                  </InputAdornment>,
                  sx: { borderRadius: 2 },
                }}
                sx={{ mb: 2 }}
              />
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 2 } }}>
                {filteredUsers.length === 0 && !loadingUsers && (
                  <Typography variant="caption" color="text.secondary" sx={{ py: 2, textAlign: "center", display: "block" }}>
                    Aucun utilisateur trouvé
                  </Typography>
                )}
                {filteredUsers.map(u => (
                  <UserCard key={u.id} user={u}
                    selected={String(u.id) === String(userId)}
                    onClick={() => setUserId(String(u.id))} />
                ))}
              </Stack>
            </Box>
          </Paper>

          {/* ── Sélection pack ── */}
          <Paper elevation={0} sx={{
            border: "1px solid", borderColor: step >= 2 ? "divider" : "#f1f5f9",
            borderRadius: 3, overflow: "hidden",
            opacity: !userId ? 0.5 : 1,
            pointerEvents: !userId ? "none" : "auto",
            transition: "opacity .3s, border-color .3s",
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider",
                bgcolor: step === 2 ? "#fafafe" : "#fff" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "50%",
                bgcolor: packId ? "#f0fdf4" : "#fff7ed",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {packId
                  ? <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                  : <InventoryIcon sx={{ fontSize: 16, color: "#f59e0b" }} />}
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Étape 2 — Pack de crédits
              </Typography>
              {selectedPack && (
                <Chip size="small" label={selectedPack.code}
                  onDelete={() => setPackId("")}
                  sx={{ height: 22, fontSize: 11, bgcolor: "#fff7ed", color: "#b45309", ml: "auto !important" }} />
              )}
            </Stack>
            <Box sx={{ p: 2.5 }}>
              <TextField size="small" fullWidth placeholder="Rechercher un pack..."
                value={qPack} onChange={e => setQPack(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    {loadingPacks ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
                  </InputAdornment>,
                  sx: { borderRadius: 2 },
                }}
                sx={{ mb: 2 }}
              />
              <Stack spacing={1} sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 2 } }}>
                {filteredPacks.length === 0 && !loadingPacks && (
                  <Typography variant="caption" color="text.secondary" sx={{ py: 2, textAlign: "center", display: "block" }}>
                    Aucun pack trouvé
                  </Typography>
                )}
                {filteredPacks.map(p => (
                  <PackCard key={p.id} pack={p}
                    selected={String(p.id) === String(packId)}
                    onClick={() => setPackId(String(p.id))} />
                ))}
              </Stack>
            </Box>
          </Paper>
        </Stack>

        {/* Colonne droite — aperçu crédits */}
        <Box sx={{ width: { xs: "100%", lg: 320 }, flexShrink: 0 }}>
          <Paper elevation={0} sx={{
            border: "1px solid", borderColor: "divider",
            borderRadius: 3, overflow: "hidden",
            position: { lg: "sticky" }, top: { lg: 24 },
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="subtitle2" fontWeight={700}>Aperçu crédits</Typography>
              {loadingCred && <CircularProgress size={12} sx={{ ml: "auto !important" }} />}
            </Stack>

            <Box sx={{ p: 2.5 }}>
              {!userId ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <PersonIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Sélectionne un utilisateur
                  </Typography>
                </Box>
              ) : !credAvail ? (
                <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12 }}>
                  Aperçu crédits indisponible (endpoint absent).
                </Alert>
              ) : before ? (
                <Stack spacing={2}>
                  {/* KPIs actuels */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block", textTransform: "uppercase", letterSpacing: .5, fontSize: 10 }}>
                      Solde actuel
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <CreditKpi label="Restantes" value={fmt(before.summary?.remaining_minutes)}
                        color="#22c55e" icon={<TrendingUpIcon />} />
                      <CreditKpi label="Totales" value={fmt(before.summary?.total_minutes)}
                        color="#6366f1" icon={<AccessTimeIcon />} />
                      <CreditKpi label="Wallets" value={before.summary?.total_wallets ?? 0}
                        color="#3b82f6" icon={<AccountBalanceWalletIcon />} />
                    </Stack>
                  </Box>

                  {/* Pack sélectionné → preview ajout */}
                  {selectedPack && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block", textTransform: "uppercase", letterSpacing: .5, fontSize: 10 }}>
                          Après activation
                        </Typography>
                        <Box sx={{ bgcolor: "#f0fdf4", borderRadius: 2, p: 2, border: "1px solid #bbf7d0" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">Min. restantes</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" fontWeight={700} sx={{ color: "#16a34a" }}>
                                {fmt((before.summary?.remaining_minutes || 0) + (selectedPack.total_minutes || 0))}
                              </Typography>
                              <Chip size="small" label={`+${fmt(selectedPack.total_minutes)}`}
                                sx={{ height: 16, fontSize: 10, bgcolor: "#dcfce7", color: "#166534", fontWeight: 700 }} />
                            </Stack>
                          </Stack>
                          {selectedPack.validity_days && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Expire dans</Typography>
                              <Typography variant="caption" fontWeight={600} color="text.primary">
                                {selectedPack.validity_days} jours
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Diff après activation réelle */}
                  {diff && (
                    <>
                      <Divider />
                      <Box sx={{ bgcolor: "#eef2ff", borderRadius: 2, p: 2, border: "1px solid #c7d2fe" }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 14, color: "#6366f1" }} />
                          <Typography variant="caption" fontWeight={700} color="#4f46e5">
                            Activation réussie
                          </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">Δ Minutes</Typography>
                            <Typography variant="caption" fontWeight={700} color="#16a34a">+{fmt(diff.remaining)}</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">Δ Total</Typography>
                            <Typography variant="caption" fontWeight={700} color="#16a34a">+{fmt(diff.total)}</Typography>
                          </Stack>
                          {diff.wallets !== 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">Nouveau wallet</Typography>
                              <Typography variant="caption" fontWeight={700} color="#6366f1">+{diff.wallets}</Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    </>
                  )}

                  {/* Wallets existants */}
                  {before.wallets?.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: .5, fontSize: 10 }}>
                          Wallets existants ({before.wallets.length})
                        </Typography>
                        <Stack spacing={1}>
                          {before.wallets.slice(0, 3).map(w => {
                            const rem = Number(w.remaining_minutes || 0);
                            const tot = Number(w.total_minutes || 0);
                            const pct = tot > 0 ? Math.round((rem / tot) * 100) : 0;
                            const barCol = pct > 50 ? "#22c55e" : pct > 20 ? "#f59e0b" : "#ef4444";
                            return (
                              <Box key={w.id}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
                                    {w.pack_code || `Wallet #${w.id}`}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: barCol, fontWeight: 700 }}>
                                    {fmt(rem)}
                                  </Typography>
                                </Stack>
                                <LinearProgress variant="determinate" value={pct}
                                  sx={{ height: 5, borderRadius: 3, bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": { bgcolor: barCol, borderRadius: 3 } }} />
                              </Box>
                            );
                          })}
                          {before.wallets.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{before.wallets.length - 3} autre(s) wallet(s)
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#6366f1" }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Chargement des crédits...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>

      {/* Dialog confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doActivate}
        busy={busy}
        user={selectedUser}
        pack={selectedPack}
      />

      {/* Snackbar */}
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack?.sev || "info"} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
